import { orderBy } from "lodash";
import { ITemplateData } from "../interfaces/PublicViewTemplateData";
import { PublicViewTemplate } from "../models/PublicViewTemplate";
import { SelectedThemeAndTemplateData } from "../components/shop/popups/SelectTemplatePopup/TemplateTabs";
import { IShop } from "../interfaces/Shop";
import { IOrder } from "../interfaces/Order";
import { TNumber } from "../interfaces/common";

interface EntityValue {
  entity: IShop | IOrder;
  order?: IOrder;
  entityId: string;
  entityNumber: TNumber;
  entityIsFree: TNumber;
  entityBuyInventory: boolean;
  entityShowInventory: boolean;
  entityShowProductWarnings: TNumber;
  entityAggregate: TNumber;
  entityClientId: string;
  entityTenantId: string;
  entityForceMinimumQty: TNumber;
  entityTemplateId?: string;
  entityLayoutId?: string;
  perCheckoutLimit: TNumber | null;
  currencySymbol: string;
  contactName: string;
  contactEmail: string;
  title: string;
  entityType: string;
  allowCouponCheckout: boolean;
  checkoutMessage: string;
  freeItemsCount: TNumber;
  allowMultipleFreeItem: boolean;
  isShop?: boolean;
  isOrder?: boolean;
}

export const useLayoutUpdated = (
  entityValue: EntityValue,
  templateData: ITemplateData,
  selectedTheme?: SelectedThemeAndTemplateData,
  selectedTemplate?: SelectedThemeAndTemplateData
) => {
  const propertiesKeys = Object.keys(selectedTemplate?.properties || {});
  if (!selectedTemplate || propertiesKeys.length === 0) {
    return false;
  }
  let matchedCategories = true;
  const categories = orderBy(
    (entityValue?.order?.items || []).filter(
      (i) => i.parent_type === "SEPARATOR"
    ),
    [(i) => parseInt(i.display_order.toString())],
    "asc"
  );

  const hasHeroImages =
    PublicViewTemplate.CATEGORIES_CAROUSEL ===
    selectedTheme?.public_view_template_name;
  const templateHasCC = [
    PublicViewTemplate.CATEGORIES_CAROUSEL,
    PublicViewTemplate.LEFT_NAV_HERO_IMAGE,
  ].includes(selectedTheme?.public_view_template_name);
  if (templateHasCC && categories.length > 0) {
    const templateCategoryImagesLength = propertiesKeys.filter(
      (k) => (k.match("^CATEGORY-([0-9]+)-IMAGE-MAIN$")?.length ?? 0) > 1
    ).length;

    if (templateCategoryImagesLength > categories.length) {
      return true;
    }

    const templateCategoryImages = propertiesKeys
      .filter(
        (k) =>
          (k.match(
            "^CATEGORY-([0-9]+)-(IMAGE-TITLE|IMAGE-MAIN|IMAGE-HOVER|IMAGE-BANNER|BANNER-DIM)$"
          )?.length ?? 0) > 1
      )
      .map((k) => selectedTemplate?.properties?.[k]);

    for (const v of templateCategoryImages) {
      const key = v.public_view_template_property_name;
      const keyIndex = parseInt(
        key
          .replace(/^CATEGORY-/gi, "")
          .replace(
            /-(IMAGE-TITLE|IMAGE-MAIN|IMAGE-HOVER|IMAGE-BANNER|BANNER-DIM)$/gi,
            ""
          )
      );

      const category = categories?.[keyIndex - 1];
      if (!category) {
        return true;
      }

      if (key.endsWith("-IMAGE-TITLE")) {
        if (category.item_name !== v.public_view_template_property_value) {
          return true;
        }
        continue;
      }

      const names = [
        "CATEGORY-" + category.item_id + "-IMAGE-MAIN",
        "CATEGORY-" + category.item_id + "-IMAGE-HOVER",
        "CATEGORY-" + category.item_id + "-IMAGE-BANNER",
        "CATEGORY-" + category.item_id + "-BANNER-DIM",
      ];
      let foundValue = null;
      for (const name of names) {
        if (templateData[name]) {
          foundValue = templateData[name];
          break;
        }
      }
      if (!foundValue) {
        return true;
      }
      matchedCategories =
        matchedCategories &&
        foundValue === v.public_view_template_property_value;
      if (!matchedCategories) {
        return true;
      }
    }
  }

  const otherPropertiesKeys = propertiesKeys.filter(
    (k) =>
      !(
        (k.match(
          "^CATEGORY-([0-9]+)-(IMAGE-MAIN|IMAGE-HOVER|IMAGE-BANNER|BANNER-DIM)$"
        )?.length ?? 0) > 1
      )
  );
  const templateDataPropsMatched = otherPropertiesKeys.filter(
    (k) => selectedTemplate.properties[k]?.value === templateData?.[k]
  );

  const heroImageKeys =
    hasHeroImages &&
    propertiesKeys.filter(
      (k) => (k.match("^hero([0-9]+)-image$")?.length ?? 0) > 1
    );
  const templateDataHeroImageKeys =
    hasHeroImages &&
    Object.keys(templateData || {}).filter(
      (k) => (k.match("^hero([0-9]+)-image$")?.length ?? 0) > 1
    );

  return (
    otherPropertiesKeys.length !== templateDataPropsMatched.length ||
    (heroImageKeys.length > 0 &&
      heroImageKeys.length !== templateDataHeroImageKeys.length) ||
    !matchedCategories
  );
};
